import { useEffect, useMemo, useState } from "react"
import { View, Pressable } from "react-native"
import MinusCircle from "@/assets/icons/checkbox-green-checked.svg"
import PlusCircle from "@/assets/icons/plus-circle.svg"
import { type AnalysisCompType } from "@/generated/graphql"
import { type AnalysisWithCompsType } from "@/queries/analysis"

import { compCompareText, type CompCompareTextOption } from "@/utils/CompUtils"
import useCompsData from "@/hooks/useCompsData"
import { useStore } from "@nanostores/react"
import {
  $selectedCompDetailId,
  compUpdatingState,
  setCompUpdatingState,
} from "@/stores/compsDataStore"
import * as AnalyzerEvents from "@/analytics/events/analyzer"
import { Body, H5 } from "@/components/standard/BFText"

interface PropertyInfoDrawerProps {
  analysis: AnalysisWithCompsType | null
}

const PropertyInfoDrawer: React.FC<PropertyInfoDrawerProps> = ({
  analysis,
}: PropertyInfoDrawerProps) => {
  // Just force a render on updating states..
  const updatingState = useStore(compUpdatingState)
  const selectedCompDetailId = useStore($selectedCompDetailId)
  const comp = useMemo(
    () => analysis?.comps?.find((c) => c?.arvcomp?.id === selectedCompDetailId),
    [selectedCompDetailId, updatingState]
  )

  const [isSelected, setIsSelected] = useState(comp?.isSelected)
  useEffect(() => {
    setIsSelected(comp?.isSelected)
  }, [comp])

  const { handleToggleSelection } = useCompsData(analysis?.property)

  if (!comp?.arvcomp) {
    return null
  }

  const onToggleSelection = (analysisId: string, toggledCompId: string) => {
    setCompUpdatingState(true)
    handleToggleSelection(analysisId, toggledCompId)
  }

  const onSelect = () => {
    if (selectedCompDetailId && analysis) {
      onToggleSelection(analysis?.id, selectedCompDetailId)
      setIsSelected(!isSelected)
      if (isSelected) {
        AnalyzerEvents.compRemoved({
          currentScreen: "Comp Detail",
          analysis,
          comp: {
            id: comp.id,
            address: comp.arvcomp?.fullAddress || "",
          },
        })
      } else {
        AnalyzerEvents.compAdded({
          currentScreen: "Comp Detail",
          analysis,
          comp: {
            id: comp.id,
            address: comp.arvcomp?.fullAddress || "",
          },
        })
      }
    }
  }

  if (!analysis || !selectedCompDetailId) {
    return <></>
  }

  const renderRow = (...input: string[]) => input.filter(Boolean).join(" • ")
  const compLabel = (label: CompCompareTextOption) =>
    compCompareText(label, comp as AnalysisCompType, analysis) || ""

  return (
    <View className="flex flex-row flex-1 pt-0 px-6 bg-midnight-blue">
      <View className="flex flex-col flex-1">
        <H5 className="text-white">{compLabel("soldPrice")}</H5>
        <H5 className="text-white">{comp.arvcomp.street}</H5>
        <H5 className="text-white">{renderRow(compLabel("cityStateZip"))}</H5>
        <Body className="text-white">
          {renderRow(
            compLabel("beds"),
            compLabel("baths"),
            compLabel("squareFeet"),
            compLabel("yearBuilt")
          )}
        </Body>
        <Body className="text-white">
          {renderRow(
            compLabel("status"),
            compLabel("soldDate"),
            compLabel("daysOnMarket"),
            compLabel("distance"),
            compLabel("pricePerSqft")
          )}
        </Body>
      </View>

      <Pressable
        testID={`${isSelected ? "remove" : "add"}-comp-button`}
        onPress={onSelect}
        hitSlop={32}
      >
        <View className="justify-center items-center">
          {isSelected ? (
            <MinusCircle width={32} height={32} />
          ) : (
            <PlusCircle width={32} height={32} />
          )}
        </View>
      </Pressable>
    </View>
  )
}

export default PropertyInfoDrawer
